import React, { useEffect, useRef, useState } from 'react';
import { Button, Input } from '@roger/ui-components';

import { Check, Edit, Loader2 } from 'lucide-react';

import { useAdminSectionAccess } from '../../hooks/useAdminSectionAccess';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../store';
import { updateAgencyWrittenName } from '../../services/api';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';

/** Admin section for the agency print / letterhead name. */
const AgencyPrintNameSection: React.FC = () => {
  const { canEdit } = useAdminSectionAccess('agency-print-name');
  const { writtenNameFromStore, showSuccess, showError, checkAuthStatus } =
    useStore(
      useShallow(state => ({
        writtenNameFromStore: state.user?.agency?.written_name ?? '',
        showSuccess: state.showSuccess,
        showError: state.showError,
        checkAuthStatus: state.checkAuthStatus,
      }))
    );

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [draft, setDraft] = useState(writtenNameFromStore);
  const inputRef = useRef<HTMLInputElement>(null);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const saving = useRef(false);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isEditing) {
      setDraft(writtenNameFromStore);
    }
  }, [writtenNameFromStore, isEditing]);

  const trimmed = draft.trim();
  const blank = trimmed.length === 0;
  const editing = isEditing && canEdit;

  const wasEditing = useRef(false);
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    } else if (wasEditing.current) {
      editButtonRef.current?.focus();
    }
    wasEditing.current = editing;
  }, [editing]);

  const startEditing = () => {
    setDraft(writtenNameFromStore);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setDraft(writtenNameFromStore);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (blank || saving.current) return;
    saving.current = true;
    setIsSaving(true);
    try {
      await updateAgencyWrittenName({
        body: { written_name: trimmed },
        throwOnError: true,
      });
      // Refresh shared agency state even if this section unmounted mid-request.
      await checkAuthStatus();
      if (!mounted.current) return;
      setIsEditing(false);
      showSuccess('Print name updated');
    } catch (err) {
      console.error('Failed to update agency print name:', err);
      if (mounted.current) {
        showError(getBusinessErrorMessage(err) ?? "Couldn't save print name");
      }
    } finally {
      saving.current = false;
      if (mounted.current) setIsSaving(false);
    }
  };

  return (
    <div className='space-y-6'>
      <div className='space-y-1'>
        <p className='text-sm text-muted-foreground'>
          Shown in the sidebar and on printed visits, orders, communications,
          the CMS-485 and claims.
        </p>
      </div>

      <div className='grid gap-2'>
        <label htmlFor='agency-print-name' className='text-sm font-medium'>
          Print name
        </label>
        {editing ? (
          <>
            <Input
              ref={inputRef}
              id='agency-print-name'
              value={draft}
              onChange={e => setDraft(e.target.value)}
              placeholder='Legal or letterhead name'
              maxLength={255}
              disabled={isSaving}
              aria-invalid={blank}
              aria-describedby={blank ? 'agency-print-name-hint' : undefined}
            />
            {blank && (
              <p
                id='agency-print-name-hint'
                className='text-xs text-destructive'
              >
                Print name is required.
              </p>
            )}
          </>
        ) : (
          <div
            id='agency-print-name'
            className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
          >
            <span>{writtenNameFromStore || '—'}</span>
          </div>
        )}
      </div>

      <div className='flex flex-wrap items-center justify-end gap-2'>
        {editing ? (
          <div className='flex gap-2'>
            <Button
              variant='outline'
              onClick={cancelEditing}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleSave()}
              disabled={blank}
              aria-busy={isSaving}
              aria-describedby={blank ? 'agency-print-name-hint' : undefined}
            >
              {isSaving ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Saving...
                </>
              ) : (
                <>
                  <Check className='mr-2 h-4 w-4' />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        ) : (
          canEdit && (
            <Button
              ref={editButtonRef}
              variant='outline'
              onClick={startEditing}
            >
              <Edit className='mr-2 h-4 w-4' />
              Edit
            </Button>
          )
        )}
      </div>
    </div>
  );
};

export default AgencyPrintNameSection;
